import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiClient } from '../../lib/api';
import { AuthLayout } from '../../components/layouts/AuthLayout';
import { slideUp } from '@/lib/motion';

const EnterCodePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // If no email in state, redirect back to forgot password
    if (!email) {
      navigate('/auth/forgot-password');
    } else {
      // Auto-focus first input
      inputRefs.current[0]?.focus();
    }
  }, [email, navigate]);

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newCode.every(digit => digit !== '')) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Only process if it's exactly 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      setError('');
      // Focus last input
      inputRefs.current[5]?.focus();
      // Auto-submit
      handleSubmit(pastedData);
    }
  };

  const handleSubmit = async (codeValue: string) => {
    if (attempts >= 3) {
      setError('Maximum attempts exceeded. Please request a new code.');
      return;
    }

    // Navigate to reset password page with code and email
    navigate('/auth/reset-password', {
      state: { email, code: codeValue },
    });
  };

  const handleManualSubmit = () => {
    const codeValue = code.join('');
    if (codeValue.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }
    handleSubmit(codeValue);
  };

  const handleResendCode = async () => {
    setResending(true);
    setResent(false);
    setError('');
    try {
      await apiClient.post('/auth/forgot-password', { email });
      setResent(true);
      setCode(['', '', '', '', '', '']);
      setAttempts(0);
      inputRefs.current[0]?.focus();
      setTimeout(() => setResent(false), 4000);
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 429) {
        setError(err.response?.data?.message || 'Too many requests. Please try again later.');
      } else {
        // Show success anyway to prevent email enumeration
        setResent(true);
        setTimeout(() => setResent(false), 4000);
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout>
      <motion.div {...slideUp}>
      {/* Icon */}
      <div className="flex justify-center mb-6">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center">
          <svg
            width="96"
            height="96"
            viewBox="0 0 96 96"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M16 20V39.8198C16 42.4386 17.2723 44.8872 19.4628 46.3224C26.2334 50.7584 41.16 60 48 60C54.84 60 69.7666 50.7584 76.5372 46.3224C78.7276 44.8872 80 42.4386 80 39.8198V20C80 13.3726 74.6274 8 68 8H28C21.3726 8 16 13.3726 16 20ZM31 20.0002C29.3432 20.0002 28 21.3434 28 23.0002C28 24.657 29.3432 26.0002 31 26.0002H49C50.6568 26.0002 52 24.657 52 23.0002C52 21.3434 50.6568 20.0002 49 20.0002H31ZM31 34.0002C29.3432 34.0002 28 35.3434 28 37.0002C28 38.657 29.3432 40.0002 31 40.0002H61C62.6568 40.0002 64 38.657 64 37.0002C64 35.3434 62.6568 34.0002 61 34.0002H31Z"
              className="fill-primary-200"
            />
            <path
              d="M28 23.0002C28 21.3434 29.3432 20.0002 31 20.0002H49C50.6568 20.0002 52 21.3434 52 23.0002C52 24.657 50.6568 26.0002 49 26.0002H31C29.3432 26.0002 28 24.657 28 23.0002Z"
              className="fill-primary-500"
            />
            <path
              d="M28 37.0002C28 35.3434 29.3432 34.0002 31 34.0002H61C62.6568 34.0002 64 35.3434 64 37.0002C64 38.657 62.6568 40.0002 61 40.0002H31C29.3432 40.0002 28 38.657 28 37.0002Z"
              className="fill-primary-500"
            />
            <path
              d="M16 25.0042C13.962 26.5169 12.1609 27.9005 10.7158 29.0306C8.30778 30.9136 6.79834 33.6906 6.59464 36.7406C6.31746 40.891 6 47.8582 6 58.0002C6 67.5914 6.56784 74.3432 7.09838 78.5586C7.53582 82.0342 9.86276 84.7904 13.2935 85.4986C18.7945 86.634 29.2988 88.0002 48 88.0002C66.7012 88.0002 77.2056 86.634 82.7064 85.4986C86.1372 84.7904 88.4642 82.0342 88.9016 78.5586C89.4322 74.3432 90 67.5914 90 58.0002C90 47.8582 89.6826 40.891 89.4054 36.7406C89.2016 33.6906 87.6922 30.9136 85.2842 29.0306C83.8391 27.9005 82.038 26.5169 80 25.0042V39.82C80 42.4388 78.7276 44.8874 76.5372 46.3226C69.7666 50.7586 54.84 60.0002 48 60.0002C41.16 60.0002 26.2334 50.7586 19.4628 46.3226C17.2723 44.8874 16 42.4388 16 39.82V25.0042Z"
              className="fill-primary-500"
            />
          </svg>
        </div>
      </div>

      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-[26px] font-bold text-text-primary mb-2">
          Please Check Your Email!
        </h1>
        <p className="text-sm text-text-secondary leading-relaxed">
          We&apos;ve emailed a 6-digit confirmation code. Please enter the code in the box below to
          verify your email.
        </p>
      </div>

      {/* Email display */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-text-primary mb-2">
          Email address
        </label>
        <div className="w-full h-[52px] px-4 flex items-center justify-between text-sm border border-border-primary/30 rounded-lg bg-neutral-50 dark:bg-neutral-800">
          <span className="text-text-secondary truncate">{email}</span>
          <button
            type="button"
            onClick={() => navigate('/auth/forgot-password')}
            className="shrink-0 ml-2 text-xs font-medium text-primary-500 hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
          >
            Edit
          </button>
        </div>
      </div>


      {/* Attempt Warning */}
      {attempts > 0 && attempts < 3 && (
        <div className="mb-5 p-3.5 bg-warning-50 dark:bg-warning-900/10 text-warning-600 dark:text-warning-400 rounded-lg text-sm">
          <p>
            {3 - attempts} attempt{3 - attempts !== 1 ? 's' : ''} remaining
          </p>
        </div>
      )}

      {/* Code Input */}
      <div className="mb-6">
        <div className="flex gap-2.5 justify-center">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              disabled={attempts >= 3}
              className={`w-12 h-14 text-center text-xl font-semibold border-2 rounded-lg transition-colors focus:outline-none ${
                error
                  ? 'border-error-500 focus:border-error-500'
                  : 'border-border-primary/30 focus:border-primary-500'
              } bg-transparent text-text-primary disabled:opacity-50`}
            />
          ))}
        </div>
      </div>

      {/* Error Message — above verify button */}
      {error && (
        <div className="mb-4 p-3.5 bg-error-50 dark:bg-error-900/10 text-error-600 dark:text-error-400 rounded-lg text-sm">
          <p>{error}</p>
        </div>
      )}

      {/* Verify Button */}
      <button
        onClick={handleManualSubmit}
        disabled={code.some((digit) => digit === '') || attempts >= 3}
        className="w-full h-12 rounded-lg bg-primary-500 font-bold text-[15px] hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-5"
      >
        Verify
      </button>

      {/* Resend Code */}
      <div className="text-center mb-4">
        {resent ? (
          <p className="text-sm text-success-600 dark:text-success-400 font-medium">
            Code sent! Check your inbox.
          </p>
        ) : (
          <p className="text-sm text-text-secondary">
            Don&apos;t have a code?{' '}
            <button
              onClick={handleResendCode}
              disabled={resending}
              className="text-primary-500 font-medium hover:underline disabled:opacity-50"
            >
              {resending ? 'Sending...' : 'Resend code'}
            </button>
          </p>
        )}
      </div>

      {/* Back to Login */}
      <div className="text-center">
        <Link
          to="/auth/login"
          className="text-sm text-text-secondary hover:text-text-primary transition-colors inline-flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Return to sign in
        </Link>
      </div>
      </motion.div>
    </AuthLayout>
  );
};

export default EnterCodePage;
